import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useSearchStore } from '@shared/stores/searchStore';
import { usePortalIds } from '@shared/config';
import { useURLFilterSync } from '@shared/hooks/useURLFilterSync';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useFilterToggle } from '@shared/hooks/useFilterToggle';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';

import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { ScrollArea } from '@shared/components/ui/scroll-area';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@shared/components/ui/drawer';
import {
  Search,
  Filter,
  Calendar,
  Building,
  Scale,
  Gavel,
  SlidersHorizontal,
  X,
  Loader2,
  AlertCircle,
  BookOpen,
  Tags,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { SearchFilters } from '@shared/components/search/SearchFilters';
import { SearchResults } from '@shared/components/search/SearchResults';
import type { SearchResult } from '@shared/components/search/SearchResultCard';
import { useSearchCases } from '@shared/hooks/useJurisprudentie';
import type { JurisprudentieWithHighlight } from '@shared/services/jurisprudentie.service';
import { CaseType, DateRange } from '@shared/constants/searchConstants';
import type { CaseTypeValue, DateRangeValue, SortByValue } from '@shared/types/searchTypes';

function SearchPage() {
  const navigate = useNavigate();

  // Get portal IDs for multi-portal filtering
  const portalIds = usePortalIds();

  // Zustand store hooks
  const {
    filters,
    metadata,
    currentPage,
    itemsPerPage,
    hasSearched,
    drawerOpen,
    setFilters,
    updateFilter,
    setCourtName,
    setDomainName,
    setThemaName,
    setWetsartikelName,
    setCaseTypeName,
    setCurrentPage,
    setHasSearched,
    setDrawerOpen,
    hasActiveFilters,
    getActiveFilterCount,
    clearFilters
  } = useSearchStore();

  // Initialize URL sync (one-way: Zustand -> URL)
  useURLFilterSync();

  // Collapse/expand state for filter badges
  const [showAllFilters, setShowAllFilters] = useState(false);
  const MAX_VISIBLE_BADGES = 10;

  // Destructure filters for easier access
  const {
    query: searchQuery,
    courts: selectedCourts,
    domains: selectedDomains,
    themas: selectedThemas,
    wetsartikelen: selectedWetsartikelen,
    caseType,
    dateRange,
    customDateFrom,
    customDateTo,
    sortBy
  } = filters;

  // Memoize customDateRange to prevent new object references on every render
  const customDateRange = useMemo(
    () => ({ from: customDateFrom, to: customDateTo }),
    [customDateFrom, customDateTo]
  );

  // Filter toggle hooks
  const { handleToggle: handleCourtToggle, handleBulkChange: handleCourtsChange } = useFilterToggle(
    selectedCourts,
    updateFilter,
    setCourtName,
    'courts'
  );

  const { handleToggle: handleThemaToggle, handleBulkChange: handleThemasChange } = useFilterToggle(
    selectedThemas,
    updateFilter,
    setThemaName,
    'themas'
  );

  const { handleToggle: handleWetsartikelToggle, handleBulkChange: handleWetsartikelenChange } = useFilterToggle(
    selectedWetsartikelen,
    updateFilter,
    setWetsartikelName,
    'wetsartikelen'
  );

  const { handleToggle: handleDomainToggle } = useFilterToggle(
    selectedDomains,
    updateFilter,
    setDomainName,
    'domains'
  );

  // Debounce the search query to prevent excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 800);

  // Memoize the active filters check to prevent unnecessary re-renders
  const hasFiltersActive = useMemo(() => {
    return (
      debouncedSearchQuery.trim() !== '' ||
      selectedDomains.length > 0 ||
      selectedCourts.length > 0 ||
      selectedThemas.length > 0 ||
      selectedWetsartikelen.length > 0 ||
      caseType !== CaseType.ALL ||
      dateRange !== DateRange.ALL
    );
  }, [
    debouncedSearchQuery,
    selectedDomains,
    selectedCourts,
    selectedThemas,
    selectedWetsartikelen,
    caseType,
    dateRange
  ]);

  // Auto-trigger search when debounced query or filters change
  // NOTE: Page resets happen in updateFilter() which is called by individual filter handlers
  useEffect(() => {
    // Only trigger search if there's a query or active filters
    if (hasFiltersActive) {
      setHasSearched(true);
    }
  }, [hasFiltersActive, setHasSearched]);

  // Use React Query hook for searching with debounced query
  const { data, isLoading, error } = useSearchCases(
    debouncedSearchQuery,
    {
      selectedDomains,
      selectedCourts,
      selectedThemas,
      selectedWetsartikelen,
      dateRange,
      customDateRange,
      sortBy,
      caseType
    },
    currentPage,
    itemsPerPage,
    hasSearched, // Only enable query when user has initiated a search
    portalIds  // Multi-portal filtering support
  );

  const handleCaseTypeChange = useCallback((value: string, typeName?: string) => {
    updateFilter('caseType', value as CaseTypeValue);
    if (typeName && value !== 'all') {
      setCaseTypeName(value, typeName);
    }
  }, [updateFilter, setCaseTypeName]);

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    setHasSearched(true);
  }, [setCurrentPage, setHasSearched]);

  // Memoized badge collection to prevent unnecessary re-renders
  const allFilterBadges = useMemo(() => {
    const badges: React.ReactElement[] = [];

    // Date badge
    if (dateRange !== DateRange.ALL) {
      badges.push(
        <Badge
          key="date-range"
          variant="secondary"
          className="cursor-pointer hover:bg-secondary/80"
          onClick={() => {
            setFilters({
              dateRange: DateRange.ALL,
              customDateFrom: undefined,
              customDateTo: undefined
            });
          }}
        >
          <Calendar className="h-3 w-3 mr-1" />
          {dateRange === DateRange.WEEK && 'Afgelopen week'}
          {dateRange === DateRange.MONTH && 'Afgelopen maand'}
          {dateRange === DateRange.YEAR && 'Afgelopen jaar'}
          {dateRange === DateRange.CUSTOM && 'Aangepaste periode'}
          <X className="h-3 w-3 ml-1" />
        </Badge>
      );
    }

    // Court badges
    selectedCourts.forEach(courtId => {
      badges.push(
        <Badge
          key={`court-${courtId}`}
          variant="secondary"
          className="cursor-pointer hover:bg-secondary/80"
          onClick={() => handleCourtToggle(courtId)}
        >
          <Building className="h-3 w-3 mr-1" />
          {metadata.courtNames[courtId] || courtId}
          <X className="h-3 w-3 ml-1" />
        </Badge>
      );
    });

    // Domain badges
    selectedDomains.forEach(domainId => {
      badges.push(
        <Badge
          key={`domain-${domainId}`}
          variant="secondary"
          className="cursor-pointer hover:bg-secondary/80"
          onClick={() => handleDomainToggle(domainId)}
        >
          <Scale className="h-3 w-3 mr-1" />
          {metadata.domainNames[domainId] || domainId}
          <X className="h-3 w-3 ml-1" />
        </Badge>
      );
    });

    // Wetsartikelen badges
    selectedWetsartikelen.forEach(artikelId => {
      badges.push(
        <Badge
          key={`wetsartikel-${artikelId}`}
          variant="secondary"
          className="cursor-pointer hover:bg-secondary/80"
          onClick={() => handleWetsartikelToggle(artikelId)}
        >
          <BookOpen className="h-3 w-3 mr-1" />
          {metadata.wetsartikelenNames[artikelId] || artikelId}
          <X className="h-3 w-3 ml-1" />
        </Badge>
      );
    });

    // Thema badges
    selectedThemas.forEach(themaId => {
      badges.push(
        <Badge
          key={`thema-${themaId}`}
          variant="secondary"
          className="cursor-pointer hover:bg-secondary/80"
          onClick={() => handleThemaToggle(themaId)}
        >
          <Tags className="h-3 w-3 mr-1" />
          {metadata.themaNames[themaId] || themaId}
          <X className="h-3 w-3 ml-1" />
        </Badge>
      );
    });

    // Case type badge
    if (caseType !== CaseType.ALL) {
      badges.push(
        <Badge
          key="case-type"
          variant="secondary"
          className="cursor-pointer hover:bg-secondary/80"
          onClick={() => handleCaseTypeChange(CaseType.ALL)}
        >
          <Gavel className="h-3 w-3 mr-1" />
          {metadata.caseTypeNames[caseType] || caseType}
          <X className="h-3 w-3 ml-1" />
        </Badge>
      );
    }

    return badges;
  }, [
    dateRange,
    selectedCourts,
    selectedDomains,
    selectedWetsartikelen,
    selectedThemas,
    caseType,
    metadata.courtNames,
    metadata.domainNames,
    metadata.wetsartikelenNames,
    metadata.themaNames,
    metadata.caseTypeNames,
    setFilters,
    handleCourtToggle,
    handleDomainToggle,
    handleThemaToggle,
    handleWetsartikelToggle,
    handleCaseTypeChange,
  ]);

  const handleResultClick = (result: SearchResult) => {
    navigate(`/case/${result.id}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Convert database items to SearchResult format
  const mapToSearchResult = (caseItem: JurisprudentieWithHighlight): SearchResult => {
    const rechtsgebied = caseItem.jurisprudentie_rechtsgebieden?.[0]?.rechtsgebieden?.naam || 'Algemeen';

    return {
      id: caseItem.id,
      ecli: caseItem.ecli,
      title: caseItem.titel,
      date: new Date(caseItem.uitspraak_datum).toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
      court: caseItem.instanties?.naam || 'Onbekende instantie',
      domain: rechtsgebied,
      summary: caseItem.samenvatting || 'Geen samenvatting beschikbaar',
      relevance: caseItem.relevance_score || 0,
      rechtsgebieden: caseItem.jurisprudentie_rechtsgebieden?.map(jr => ({
        naam: jr.rechtsgebieden?.naam || ''
      })),
      zaak_types: caseItem.jurisprudentie_zaak_types?.map(jz => ({
        naam: jz.zaak_types?.naam || ''
      })),
      wetsartikelen: caseItem.jurisprudentie_wetsartikelen?.map(jw => ({
        volledige_referentie: jw.wetsartikelen?.volledige_referentie || ''
      })),
      themas: caseItem.jurisprudentie_themas?.map(jt => ({
        naam: jt.themas?.naam || ''
      })),
      portal: caseItem.portal,
      // New highlight fields
      relevance_score: caseItem.relevance_score,
      titel_highlight: caseItem.titel_highlight,
      samenvatting_highlight: caseItem.samenvatting_highlight,
      tekst_highlight: caseItem.tekst_highlight,
      // Fuzzy highlighting - words that matched via word_similarity
      fuzzy_matched_words: caseItem.fuzzy_matched_words
    };
  };


  const hasFilters = hasActiveFilters();
  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="min-h-screen bg-background">
      {/* Search Header */}
      <div className="border-b border-border/30 bg-card shadow-sm">
        <div className="container mx-auto px-2 py-6">
          <div className="flex flex-col gap-4">

            {/* Mobile Filter Button */}
            <div className="flex items-center justify-between md:hidden">
              <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                <DrawerTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    Filters
                    {hasFilters && (
                      <Badge className="ml-2 px-1.5 py-0.5 text-xs" variant="secondary">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="max-h-[85vh]">
                  <DrawerHeader>
                    <DrawerTitle className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      Filters
                    </DrawerTitle>
                    <DrawerDescription>
                      Verfijn je zoekresultaten
                    </DrawerDescription>
                  </DrawerHeader>
                  <ScrollArea className="px-4 max-h-[60vh]">
                    <SearchFilters
                      searchQuery={debouncedSearchQuery}
                      selectedDomains={selectedDomains}
                      selectedCourts={selectedCourts}
                      selectedThemas={selectedThemas}
                      selectedWetsartikelen={selectedWetsartikelen}
                      dateRange={dateRange}
                      caseType={caseType}
                      customDateRange={customDateRange}

                      onCourtToggle={handleCourtToggle}
                      onCourtsChange={handleCourtsChange}
                      onThemaToggle={handleThemaToggle}
                      onThemasChange={handleThemasChange}
                      onWetsartikelToggle={handleWetsartikelToggle}
                      onWetsartikelenChange={handleWetsartikelenChange}
                      onDateRangeChange={(value) => updateFilter('dateRange', value as DateRangeValue)}
                      onCustomDateRangeChange={(value) => setFilters({
                        customDateFrom: value.from,
                        customDateTo: value.to
                      })}
                      onCaseTypeChange={handleCaseTypeChange}
                      isMobile={true}
                    />
                  </ScrollArea>
                  <DrawerFooter>
                    <Button onClick={() => setDrawerOpen(false)}>Toepassen</Button>
                    <Button variant="outline" onClick={() => {
                      clearFilters();
                      setDrawerOpen(false);
                    }}>
                      Filters wissen
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </div>

            {/* Search Bar */}
            <div className="relative max-w-4xl mx-auto w-full">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Zoek op ECLI, trefwoord of onderwerp..."
                  value={searchQuery}
                  onChange={(e) => updateFilter('query', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-12 pr-32 h-14 text-base bg-background border-border rounded-xl shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-1.5 right-1.5">
                  <Button
                    className="h-full px-6 rounded-lg shadow-sm"
                    onClick={handleSearch}
                  >
                    Zoeken
                  </Button>
                </div>
              </div>
            </div>

            {/* Metadata Loading Indicator */}
            {metadata.isLoadingMetadata && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Filtergegevens laden...</span>
              </div>
            )}

            {/* Active Filters */}
            {hasFilters && (
              <div className="flex flex-wrap gap-2">
                {/* Render visible badges */}
                {(showAllFilters
                  ? allFilterBadges
                  : allFilterBadges.slice(0, MAX_VISIBLE_BADGES)
                )}

                {/* Expand/Collapse toggle badge */}
                {allFilterBadges.length > MAX_VISIBLE_BADGES && (
                  <Badge
                    variant="outline"
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => setShowAllFilters(!showAllFilters)}
                    role="button"
                    aria-expanded={showAllFilters}
                    aria-label={showAllFilters ? "Toon minder filters" : `Toon ${allFilterBadges.length - MAX_VISIBLE_BADGES} meer filters`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowAllFilters(!showAllFilters);
                      }
                    }}
                  >
                    {showAllFilters ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        Minder tonen
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        +{allFilterBadges.length - MAX_VISIBLE_BADGES} meer filters
                      </>
                    )}
                  </Badge>
                )}

                {/* Clear all filters badge (always visible) */}
                <Badge
                  variant="outline"
                  className="cursor-pointer hover:bg-accent"
                  onClick={clearFilters}
                >
                  <X className="h-3 w-3 mr-1" />
                  Alle filters wissen
                </Badge>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-2 py-6">
        <div className="flex gap-6">
          {/* Desktop Filters Sidebar - Sticky position (accounts for fixed header) */}
          <aside className="hidden md:block w-80 flex-shrink-0">
            <div className="sticky top-20">
              <Card className="border-border bg-card h-[calc(100vh-6rem)] flex flex-col">
                <CardHeader className="pb-4 flex-shrink-0">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Filters
                    {hasFilters && (
                      <Badge className="ml-auto" variant="secondary">
                        {activeFilterCount} actief
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full px-6 pb-6">
                    <SearchFilters
                      searchQuery={debouncedSearchQuery}
                      selectedDomains={selectedDomains}
                      selectedCourts={selectedCourts}
                      selectedThemas={selectedThemas}
                      selectedWetsartikelen={selectedWetsartikelen}
                      dateRange={dateRange}
                      caseType={caseType}
                      customDateRange={customDateRange}

                      onCourtToggle={handleCourtToggle}
                      onCourtsChange={handleCourtsChange}
                      onThemaToggle={handleThemaToggle}
                      onThemasChange={handleThemasChange}
                      onWetsartikelToggle={handleWetsartikelToggle}
                      onWetsartikelenChange={handleWetsartikelenChange}
                      onDateRangeChange={(value) => updateFilter('dateRange', value as DateRangeValue)}
                      onCustomDateRangeChange={(value) => setFilters({
                        customDateFrom: value.from,
                        customDateTo: value.to
                      })}
                      onCaseTypeChange={handleCaseTypeChange}
                      isMobile={false}
                    />
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Search Results */}
          <div className="flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : error ? (
              <div>
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {error instanceof Error ? error.message : 'Er is een fout opgetreden bij het zoeken'}
                  </AlertDescription>
                </Alert>
              </div>
            ) : !hasSearched ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Search className="h-12 w-12 text-muted-foreground mb-4" />
                <h2 className="text-xl font-semibold mb-2">Begin met zoeken</h2>
                <p className="text-muted-foreground max-w-md">
                  Voer een zoekterm in of gebruik de filters om relevante juridische uitspraken te vinden
                </p>
              </div>
            ) : !data || data.results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Search className="h-12 w-12 text-muted-foreground mb-4" />
                <h2 className="text-xl font-semibold mb-2 text-foreground">Geen resultaten gevonden</h2>
                <p className="text-muted-foreground max-w-md mb-4">
                  Probeer andere zoektermen of pas de filters aan
                </p>
                <Button variant="outline" onClick={clearFilters} className="text-foreground">
                  Filters wissen
                </Button>
              </div>
            ) : (
              <SearchResults
                results={data.results.map(mapToSearchResult)}
                totalResults={data.totalCount}
                searchQuery={searchQuery}
                sortBy={sortBy}
                onSortChange={(value) => updateFilter('sortBy', value as SortByValue)}
                onResultClick={handleResultClick}
                currentPage={currentPage}
                totalPages={data.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Export with Sentry profiler for performance monitoring during development
export default Sentry.withProfiler(SearchPage, { name: 'SearchPage' });